/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import { TextareaAutosize } from "@mui/material";
import Box from "@mui/material/Box";
import { deactivateUser } from "../../../../Services/redux/global-admin-users/GlobalAdminUsersActions";
import { useDispatch } from "react-redux";
import { modalTitleStyles } from "./UserModalStyles";
import PropTypes from "prop-types";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const StyledTextarea = styled(TextareaAutosize)({
  resize: "vertical",
  maxWidth: "423px",
  minHeight: "64px",
  padding: "12px",
  fontSize: "14px",
  borderRadius: "4px",
  border: "1px solid #12141533",
  "::placeholder": {
    color: "#717273",
    fontFamily: "Roboto",
    fontWeight: 400,
    fontSize: "14px",
    letterSpacing: "0px",
    lineHeight: "140%",
    verticalAlign: "middle",
  },
});

export default function DeactivateModal({
  selectedRowIdId,
  openDeactivateModal,
  handleDeactivateClose,
  searchTerm,
  setAnchorEl,
}) {
  const dispatch = useDispatch();
  const [deactivateReason, setDeactivateReason] = useState("");
  const [inputValue, setInputValue] = useState("");
  const debounceTimeout = useRef(null);
  const userReasonsRef = useRef({});

  // Handle selectedRowIdId changes - reset or restore reason
  useEffect(() => {
    if (selectedRowIdId) {
      // Check if we have a saved reason for this user
      const savedReason = userReasonsRef.current[selectedRowIdId] || "";
      setDeactivateReason(savedReason);
      setInputValue(savedReason);
    }
  }, [selectedRowIdId]);

  // Save reason to ref whenever it changes (debounced value)
  useEffect(() => {
    if (selectedRowIdId && deactivateReason !== undefined) {
      userReasonsRef.current[selectedRowIdId] = deactivateReason;
    }
  }, [selectedRowIdId, deactivateReason]);

  const debouncedSetReason = useCallback((value) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      setDeactivateReason(value);
    }, 100);
  }, []);

  const handleTextareaChange = (e) => {
    const value = e.target.value;
    setInputValue(value); // Update input immediately for smooth typing
    debouncedSetReason(value); // Debounce the persistent state
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const handleDeactivateUser = () => {
    // Only proceed if there's a valid reason (not empty or just whitespace)
    if (deactivateReason.trim()) {
      dispatch(
        deactivateUser(
          selectedRowIdId,
          { reason: deactivateReason.trim() },
          searchTerm?.trim()
        )
      );
      handleDeactivateClose();
      setDeactivateReason("");
      setAnchorEl(null);
    }
  };

  // Check if the deactivate reason is valid (not empty or just whitespace)
  const isReasonValid = useMemo(
    () => deactivateReason.trim().length > 0,
    [deactivateReason]
  );

  return (
      <BootstrapDialog
        onClose={handleDeactivateClose}
        aria-labelledby="customized-dialog-title"
        open={openDeactivateModal}
        slotProps={{
          paper: {
            sx: {
              width: "423px",
              minHeight: "196px",
              borderRadius: "8px",
              padding: "24px",
              display: "flex",
              gap: "24px",
            },
          },
        }}
      >
        <Box
          sx={{
            minWidth: "375px",
            minHeight: "88px",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
          }}
        >
          <div>
            <Typography
              sx={modalTitleStyles}
            >
              Deactivate User
            </Typography>
            <IconButton
              aria-label="close"
              onClick={handleDeactivateClose}
              sx={(theme) => ({
                position: "absolute",
                right: 8,
                top: 8,
                color: theme.palette.grey[500],
              })}
            >
              <CloseIcon />
            </IconButton>
          </div>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              maxWidth: "423px",
              minHeight: "87px",
            }}
          >
            <Typography
              component="label"
              sx={{
                minWidth: "122px",
                height: "19px",
                fontWeight: 600,
                fontSize: "16px",
                lineHeight: "100%",
                letterSpacing: "0px",
                verticalAlign: "middle",
                color: "#2A2C2D",
              }}
            >
              Reason to Deactivate
              {" "}
              <span style={{ marginLeft: "4px", color: "#B50B00" }}>*</span>
            </Typography>
            <StyledTextarea
              value={inputValue}
              onChange={handleTextareaChange}
              placeholder="Prompt text"
            />
          </Box>
        </Box>

        <DialogActions>
          <Button
            sx={{
              textTransform: "none",
            }}
            variant="outlined"
            onClick={handleDeactivateClose}
          >
            Cancel
          </Button>
          <Button
            sx={{
              textTransform: "none",
              color: "#B50B00",
              borderColor: "#B50B00",
              opacity: isReasonValid ? 1 : 0.5,
            }}
            variant="outlined"
            onClick={handleDeactivateUser}
            disabled={!isReasonValid}
          >
            Deactivate
          </Button>
        </DialogActions>
      </BootstrapDialog>
  );
}

DeactivateModal.propTypes = {
  selectedRowIdId: PropTypes.string,
  openDeactivateModal: PropTypes.bool,
  handleDeactivateClose: PropTypes.func,
  searchTerm: PropTypes.string,
  setAnchorEl: PropTypes.func,
};
